import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismBadge } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import {
  COLUMN_PROPERTIES,
  type ColumnProperties,
} from 'lumen/components/hotel/settings/central-mapping/tables/table-mapping';
import SettingsCentralMappingDropdownsDropdownBarBased from 'lumen/components/settings/central-mapping/dropdowns/dropdown-bar-based';
import SettingsCentralMappingDropdownsDropdownMapping from 'lumen/components/settings/central-mapping/dropdowns/dropdown-mapping';
import SettingsCentralMappingTablesCellsEditableNumberCell from 'lumen/components/settings/central-mapping/tables/cells/editable-number-cell';
import SettingsCentralMappingTablesCellsEditableTextCell from 'lumen/components/settings/central-mapping/tables/cells/editable-text-cell';
import type { CentralMappingCategory } from 'lumen/models';
import type { BarBased } from 'lumen/models/central-mapping/mapping-entry-market-segment';
import { isMappingEntryRoomType } from 'lumen/models/central-mapping/mapping-entry-room-type';
import {
  type Breakdown,
  BREAKDOWNS,
  type CentralMappingMappingEntryExtendedModel,
  mappingEntryHasBarBased,
  MAX_ADULTS_IN_ROOM,
} from 'lumen/utils/central-mapping-constants';

interface MarketSettingsCentralMappingTablesTableMappingBulkEditRowSignature {
  Args: {
    barBased: {
      setBarBased: (value: BarBased) => void;
    };
    breakdown: Breakdown;
    bulkEditRow: {
      numberOfSelectedRows: number;
    };
    mapping: {
      hierarchyCategories: CentralMappingCategory[];
      setCategory: (category: CentralMappingCategory | null) => void;
    };
    selectedMappingEntries: CentralMappingMappingEntryExtendedModel[];
  };
}

export default class MarketSettingsCentralMappingTablesTableMappingBulkEditRow extends Component<MarketSettingsCentralMappingTablesTableMappingBulkEditRowSignature> {
  @service declare centralMapping: Services['central-mapping'];
  @service declare usageAnalytics: Services['usage-analytics'];

  get bulkAutoBarBased(): BarBased {
    const { selectedMappingEntriesHasBarBased: selectedMappingEntries } = this;

    if (!selectedMappingEntries[0]) {
      return null;
    }

    const firstBarBased = selectedMappingEntries[0].autoBarBased;

    if (
      selectedMappingEntries.every(
        (entry) => entry.autoBarBased === firstBarBased,
      )
    ) {
      return firstBarBased;
    }

    return null;
  }

  get bulkAutoCategory(): CentralMappingCategory | null {
    const { selectedMappingEntries } = this.args;

    if (!selectedMappingEntries[0]) {
      return null;
    }

    const firstAutoCategory = selectedMappingEntries[0].autoCategory;

    if (
      selectedMappingEntries.every(
        (entry) => entry.autoCategory === firstAutoCategory,
      )
    ) {
      return firstAutoCategory;
    }

    return null;
  }

  get bulkBarBased(): BarBased {
    const { selectedMappingEntriesHasBarBased: selectedMappingEntries } = this;

    if (!selectedMappingEntries[0]) {
      return null;
    }

    const firstBarBased = selectedMappingEntries[0].barBased;

    if (
      selectedMappingEntries.every((entry) => entry.barBased === firstBarBased)
    ) {
      return firstBarBased;
    }

    return null;
  }

  get bulkCategory(): CentralMappingCategory | null {
    const { selectedMappingEntries } = this.args;

    if (!selectedMappingEntries[0]) {
      return null;
    }

    const firstCategory = selectedMappingEntries[0].category;

    if (
      selectedMappingEntries.every((entry) => entry.category === firstCategory)
    ) {
      return firstCategory;
    }

    return null;
  }

  get bulkUserBarBased(): BarBased {
    const { selectedMappingEntriesHasBarBased: selectedMappingEntries } = this;

    if (!selectedMappingEntries[0]) {
      return null;
    }

    // Edge case: when the barBase values are the same, but it's a mix of user-mapped and auto-mapped, the bulk edit dropdown should show the barBase as user-mapped.
    // That's why we check on `barBase` here and not `userbarBase`.
    const firstBarBased = selectedMappingEntries[0].barBased;

    if (
      selectedMappingEntries.every((entry) => entry.barBased === firstBarBased)
    ) {
      return firstBarBased;
    }

    return null;
  }

  get bulkUserCategory(): CentralMappingCategory | null {
    const { selectedMappingEntries } = this.args;

    if (!selectedMappingEntries[0]) {
      return null;
    }

    const firstUserCategory = selectedMappingEntries[0].userCategory;

    if (
      selectedMappingEntries.every(
        (entry) => entry.userCategory === firstUserCategory,
      )
    ) {
      return firstUserCategory;
    }

    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get description() {
    const { selectedMappingEntries } = this.args;

    if (!selectedMappingEntries[0]) {
      return { value: '', isMixed: false };
    }

    const firstDescription = selectedMappingEntries[0].description;

    if (
      selectedMappingEntries.every(
        (entry) => entry.description === firstDescription,
      )
    ) {
      return {
        isMixed: false,
        value: firstDescription,
      };
    }

    return {
      isMixed: true,
      value: '',
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMarketSegmentBreakdown() {
    return this.args.breakdown === BREAKDOWNS.MARKET_SEGMENT;
  }

  get isMixedBarBased(): boolean {
    const { selectedMappingEntriesHasBarBased: selectedMappingEntries } = this;

    if (!selectedMappingEntries[0]) {
      return false;
    }

    const firstBarBased = selectedMappingEntries[0].barBased;

    if (
      selectedMappingEntries.every((entry) => entry.barBased === firstBarBased)
    ) {
      return false;
    }

    return true;
  }

  get isMixedCategory(): boolean {
    const { selectedMappingEntries } = this.args;

    if (!selectedMappingEntries[0]) {
      return false;
    }

    const firstCategory = selectedMappingEntries[0].category;

    if (
      selectedMappingEntries.every((entry) => entry.category === firstCategory)
    ) {
      return false;
    }

    return true;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isRoomTypeBreakdown() {
    return this.args.breakdown === BREAKDOWNS.ROOM_TYPE;
  }

  get maxAdults(): {
    isMixed?: boolean;
    value: number | null;
  } {
    const selectedMappingEntries = this.args.selectedMappingEntries.filter(
      (entry) =>
        // @ts-expect-error: Incorrect type
        isMappingEntryRoomType(entry),
    );

    if (!selectedMappingEntries[0]) {
      return {
        value: null,
      };
    }

    // The bulk value of maxAdults is the common value in all selected mapping entries.
    // If there is no common value, the value is null and the dropdown will display "Mixed".
    const firstMaxAdults = selectedMappingEntries[0].maxAdults;

    if (
      selectedMappingEntries.every(
        (entry) => entry.maxAdults === firstMaxAdults,
      )
    ) {
      return {
        value: firstMaxAdults,
      };
    }

    return {
      value: null,
      isMixed: true,
    };
  }

  // Getter used to narrow the type of CentralMappingMappingEntryExtendedModel[],
  // otherwise we can't access the `userBarBased` or `autoCategory` properties.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedMappingEntriesHasBarBased() {
    return this.args.selectedMappingEntries.filter((mappingEntry) =>
      // @ts-expect-error: Incorrect type
      mappingEntryHasBarBased(mappingEntry),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  trackBulkUpdate(updatedColumn: ColumnProperties) {
    const rowCount = new Set(
      this.args.selectedMappingEntries.map((entry) => {
        return entry.value;
      }),
    ).size;

    this.usageAnalytics.track('Mapping Bulk Updated', {
      eventTrigger: 'bulk-changes',
      bulk: updatedColumn,
      rowCount,
    });

    this.centralMapping.trackMappingEntryChanged({
      mappingChange: updatedColumn,
      rowCount,
      rowType: 'bulk',
    });
  }

  @action setBarBased(value: BarBased | null): void {
    this.args.barBased.setBarBased(value);
    this.trackBulkUpdate(COLUMN_PROPERTIES.BAR_BASED);
  }

  @action setCategory(category: CentralMappingCategory | null): void {
    this.args.mapping.setCategory(category);
    this.trackBulkUpdate(COLUMN_PROPERTIES.MAPPING);
  }

  @action setDescription(value: string): void {
    this.args.selectedMappingEntries.forEach((entry) => {
      entry.description = value;
    });
    this.trackBulkUpdate(COLUMN_PROPERTIES.DESCRIPTION);
  }

  @action setMaxAdultsValue(value: number | null): void {
    this.args.selectedMappingEntries.forEach((entry) => {
      // @ts-expect-error: Incorrect type
      if (isMappingEntryRoomType(entry)) {
        entry.maxAdults = value;
      }
    });
    this.trackBulkUpdate(COLUMN_PROPERTIES.MAX_ADULTS);
  }

  <template>
    <th class="bulk-edit-row-cell">
      <PrismBadge
        @label={{t
          "settings.central_mapping.mapping.selected.count"
          class="text-200--bold"
          count=@bulkEditRow.numberOfSelectedRows
          htmlSafe=true
        }}
        @type="info"
      />
    </th>

    <th
      class="bulk-edit-row-cell py-0"
      data-test-id="bulk-edit-row-cell-description"
    >
      <SettingsCentralMappingTablesCellsEditableTextCell
        @cellDataTestId="bulk-action-description"
        @isMixed={{this.description.isMixed}}
        @setValue={{this.setDescription}}
        @value={{this.description.value}}
      />
    </th>

    <th class="bulk-edit-row-cell" data-test-id="bulk-edit-row-cell-property" />

    <th class="bulk-edit-row-cell" data-test-id="bulk-edit-row-cell-mapping">
      <SettingsCentralMappingDropdownsDropdownMapping
        @autoCategory={{this.bulkAutoCategory}}
        @category={{this.bulkCategory}}
        @hideAutomappingVisuals={{true}}
        @hierarchyCategories={{@mapping.hierarchyCategories}}
        @isMixed={{this.isMixedCategory}}
        @setCategory={{this.setCategory}}
        @userCategory={{this.bulkUserCategory}}
        @variant="default"
      />
    </th>

    {{#if this.isMarketSegmentBreakdown}}
      <th
        class="bulk-edit-row-cell"
        data-test-id="bulk-edit-row-cell-bar-based"
      >
        <SettingsCentralMappingDropdownsDropdownBarBased
          @autoBarBased={{this.bulkAutoBarBased}}
          @barBased={{this.bulkBarBased}}
          @hideAutomappingVisuals={{true}}
          @isMixed={{this.isMixedBarBased}}
          @setBarBased={{this.setBarBased}}
          @userBarBased={{this.bulkUserBarBased}}
          @variant="default"
        />
      </th>
    {{else if this.isRoomTypeBreakdown}}
      {{! Room type breakdown - Inventory }}
      <th class="bulk-edit-row-cell" />

      {{! Room type breakdown - No. Adults }}
      <th
        class="bulk-edit-row-cell py-0"
        data-test-id="bulk-edit-row-cell-max-adults"
      >
        <SettingsCentralMappingTablesCellsEditableNumberCell
          @cellDataTestId="bulk-action-max-adults"
          @isMixed={{this.maxAdults.isMixed}}
          @maxValue={{MAX_ADULTS_IN_ROOM}}
          @placeholder={{t
            "settings.central_mapping.mapping.max_adults.placeholder"
          }}
          @setValue={{this.setMaxAdultsValue}}
          @value={{this.maxAdults.value}}
        />
      </th>
    {{/if}}
  </template>
}
